/**
 * This file may contain code that uses generative AI for code assistance, unit testing and/or entire functions.
 * The generative model(s) used may be a combination of GitHub Copilot, OpenAI ChatGPT or others.
 */

import { createContext, useContext, useMemo } from 'react';

import {
  UNSAFE_DataRouterStateContext as DataRouterStateContext,
  UNSAFE_LocationContext as LocationContext,
  UNSAFE_RouteContext as RouteContext,
  useParams,
  useLocation,
} from 'react-router-dom';

const FallbackRouterStateContext = createContext(null);
const RouterStateContext = DataRouterStateContext ?? FallbackRouterStateContext;

const FallbackLocationContext = createContext(null);
const SafeLocationContext = LocationContext ?? FallbackLocationContext;

const normalizeRouteMatch = match => {
  if (!match || typeof match !== 'object') {
    return null;
  }

  if (match.handle !== undefined) {
    return { handle: match.handle };
  }

  return { handle: match.route?.handle };
};

export const useCommandIntelligenceRouteMatches = () => {
  const dataRouterState = useContext(RouterStateContext);
  const routeContext = useContext(RouteContext);
  const dataRouterMatches = dataRouterState?.matches;
  const routeContextMatches = routeContext?.matches;

  return useMemo(() => {
    const rawMatches = dataRouterMatches ?? routeContextMatches ?? [];

    return (rawMatches ?? []).map(normalizeRouteMatch).filter(Boolean);
  }, [dataRouterMatches, routeContextMatches]);
};

export const useCommandIntelligenceRouteParams = () => {
  const dataRouterState = useContext(RouterStateContext);
  const routeContext = useContext(RouteContext);
  const hookParams = useParams();
  const dataRouterMatches = dataRouterState?.matches;
  const routeContextMatches = routeContext?.matches;

  return useMemo(() => {
    const rawMatches = dataRouterMatches ?? routeContextMatches ?? [];
    const leafMatch = rawMatches?.[rawMatches.length - 1];
    const contextParams = leafMatch?.params ?? {};
    const hasContextParams = Object.keys(contextParams).length > 0;

    if (hasContextParams) {
      return contextParams;
    }

    return hookParams ?? {};
  }, [dataRouterMatches, routeContextMatches, hookParams]);
};

export const useCommandIntelligenceRoutePathname = () => {
  const location = useContext(SafeLocationContext);
  const hookLocation = useLocation();
  const dataRouterState = useContext(RouterStateContext);
  const routeContext = useContext(RouteContext);
  const dataRouterMatches = dataRouterState?.matches;
  const routeContextMatches = routeContext?.matches;

  return useMemo(() => {
    if (location?.pathname) {
      return location.pathname;
    }

    if (hookLocation?.pathname) {
      return hookLocation.pathname;
    }

    const rawMatches = dataRouterMatches ?? routeContextMatches ?? [];
    const leafMatch = rawMatches?.[rawMatches.length - 1];

    return leafMatch?.pathname ?? '';
  }, [location?.pathname, hookLocation?.pathname, dataRouterMatches, routeContextMatches]);
};
